import React from "react";
import * as palette from "@govlaunch/palette";
import { FormattedMessage } from "react-intl";
import { IStripePlanFieldsFragment } from "~/lib/queries/fragments/__generated__/StripePlanFields.generated";
import { IPlanRecurrency } from "~/types/types";
import PlanOfferingsBlock from "~/components/makers/PlanOfferingsBlock";
import PlanOfferings from "~/components/makers/PlanOfferings";
import { CheckEmoji, StarEmoji } from "~/components/makers/Emojis";
import Strongify from "~/lib/utils/strongify";

interface IFromPlanOptionProps {
  fromPlan: IStripePlanFieldsFragment;
}

export default function FromPlanOption({ fromPlan }: IFromPlanOptionProps) {
  return (
    <div
      css={{
        padding: 20,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 4,
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 12,
        }}
      >
        <strong
          css={{
            color: "#797979",
            fontWeight: "bold",
            fontSize: 16,
            fontStyle: "italic",
            textTransform: "uppercase",
          }}
        >
          <FormattedMessage defaultMessage="Current Plan" id="Z3GpJF" />
        </strong>

        <h2
          css={{
            margin: 0,
            fontSize: 24,
            color: "#4a4a4a",
          }}
        >
          {fromPlan.displayName}
        </h2>

        <h3
          css={{
            margin: 0,
            color: "#797979",
            fontWeight: "bold",
            fontSize: 24,
          }}
        >
          $
          {fromPlan.recurrency === IPlanRecurrency.Yearly
            ? (fromPlan.price / 100 / 12).toFixed(2)
            : (fromPlan.price / 100).toFixed(2)}
          <span
            css={{
              fontSize: 24,
              fontWeight: "normal",
              color: "#9f9f9f",
            }}
          >
            /<FormattedMessage defaultMessage="month" id="Cu3Cty" />
          </span>
        </h3>

        <div
          css={{
            padding: "10px 0px",
          }}
        >
          <PlanOfferingsBlock>
            <PlanOfferings>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Enhanced profiles for <strong>{count} products</strong>"
                  id="oQg1FL"
                  values={{
                    count: fromPlan.limits.enrichedProducts,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              {fromPlan.limits.productTags > 0 && (
                <li>
                  {CheckEmoji}{" "}
                  <FormattedMessage
                    defaultMessage="Up to <strong>{chunk} category tags</strong> per product"
                    id="DQ/iww"
                    values={{
                      chunk: `${fromPlan.limits.productTags}`,
                      strong: (chunk: any) => Strongify(chunk),
                    }}
                  />
                </li>
              )}
              <li>
                {CheckEmoji} <FormattedMessage defaultMessage="Enhanced vendor profile" id="ervosm" />
              </li>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Team access for <strong>{count} users</strong>"
                  id="Uj5Y7y"
                  values={{
                    count: fromPlan.limits.teamMembers,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Add up to <strong>{projectsPerMonth}</strong> new {projectsPerMonth, plural, one {project} other {projects}} per month"
                  id="SkEF2S"
                  values={{
                    projectsPerMonth: fromPlan.limits.projectsPerMonth,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="<strong>{count}</strong>custom branded discussion groups"
                  id="hFfG9q"
                  values={{
                    count: fromPlan.limits.groups,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Access up to <strong>{count}</strong> community discussion groups"
                  id="gpQvpj"
                  values={{
                    count: fromPlan.limits.groupInvitations,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              {fromPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji}{" "}
                  <li>
                    {CheckEmoji}{" "}
                    <FormattedMessage
                      defaultMessage="View other <strong>product profiles</strong>"
                      id="S0q+nj"
                      values={{ strong: (chunk: any) => Strongify(chunk) }}
                    />
                  </li>
                </li>
              )}
              {fromPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji} <FormattedMessage defaultMessage="Search for other vendor & products" id="vEUFjJ" />
                </li>
              )}
              {fromPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji} <FormattedMessage defaultMessage="Suggest products to add to Govlaunch" id="MV9qNV" />
                </li>
              )}
              {fromPlan.limits.sharingPage && (
                <li>
                  {StarEmoji} <FormattedMessage defaultMessage="Customizable public sharing hub" id="eTjaNG" />
                </li>
              )}
            </PlanOfferings>
          </PlanOfferingsBlock>
        </div>
      </div>
    </div>
  );
}
